"use client";

import { useEffect, useState } from "react";

export interface ReturnPolicy {
  windowDays: number;
  costPerPackage: number;
  /** Free-form "HH:MM" cutoff string used in legal copy. */
  sameDayCutoff: string;
  currency: string;
}

const FALLBACK: ReturnPolicy = {
  windowDays: 14,
  costPerPackage: 7.25,
  sameDayCutoff: "15:00",
  currency: "EUR",
};

/**
 * Returns the org's returns-policy values. Reads from
 * `/api/returns-policy`; falls back to the platform defaults
 * (14d / €7.25 / 15:00 / EUR) on transient failure so the
 * trust bar never renders an empty "X dagen retour" string.
 */
export function useReturnPolicy(): { policy: ReturnPolicy; ready: boolean } {
  const [policy, setPolicy] = useState<ReturnPolicy>(FALLBACK);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/returns-policy", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: Partial<ReturnPolicy> | null) => {
        if (body && typeof body === "object") {
          setPolicy({
            windowDays:
              typeof body.windowDays === "number" && body.windowDays > 0
                ? body.windowDays
                : FALLBACK.windowDays,
            costPerPackage:
              typeof body.costPerPackage === "number" && body.costPerPackage >= 0
                ? body.costPerPackage
                : FALLBACK.costPerPackage,
            sameDayCutoff:
              typeof body.sameDayCutoff === "string"
                ? body.sameDayCutoff
                : FALLBACK.sameDayCutoff,
            currency:
              typeof body.currency === "string" ? body.currency : FALLBACK.currency,
          });
        }
        setReady(true);
      })
      .catch(() => setReady(true));
    return () => controller.abort();
  }, []);

  return { policy, ready };
}
